/**
 * CRM KPI ダッシュボード
 *
 * CRMアクセス権を持つユーザー向けのホーム画面。
 * サマリー統計、パイプラインファネル、契約終了予定、詳細分析を表示する。
 */

import { Button } from "components/ui/Button";
import { FullScreenLoading, Loading } from "components/ui/Loading/Loading";
import type {
  AssigneePerformance,
  DashboardAlert,
  DashboardAnalyticsResponse,
  DashboardSummaryStats,
  ExpiringContract,
  PipelineFunnelStage,
} from "hooks/use-crm-dashboard-api";
import {
  useDashboardAnalytics,
  useDashboardSummary,
} from "hooks/use-crm-dashboard-api";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "utils/relative-time";

// ---------------------------------------------------------------------------
// AlertWidget
// ---------------------------------------------------------------------------

const SEVERITY_STYLES: Record<string, string> = {
  critical: "border-l-error text-error",
  warning: "border-l-warning text-warning",
  info: "border-l-info text-info",
};

const SEVERITY_BG: Record<string, string> = {
  critical: "bg-error/10",
  warning: "bg-warning/10",
  info: "bg-info/10",
};

/**
 * アラートウィジェット
 * @param {object} props
 * @param {Array} props.alerts アラート一覧
 * @return {JSX.Element} アラート表示
 */
const AlertWidget: FC<{ alerts: DashboardAlert[] }> = ({ alerts }) => {
  const navigate = useNavigate();
  const visible = alerts.slice(0, 5);

  if (visible.length === 0) {
    return (
      <div className="alert alert-success shadow-sm mb-6">
        <span>対応が必要な項目はありません</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 mb-6">
      {visible.map((alert, idx) => (
        <div
          key={`${alert.type}-${alert.talentId}-${idx}`}
          className={`card card-compact border-l-4 ${SEVERITY_STYLES[alert.severity] ?? ""} ${SEVERITY_BG[alert.severity] ?? ""}`}
        >
          <div className="card-body flex-row items-center justify-between py-2">
            <span className="text-sm">{alert.message}</span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => navigate(alert.link)}
            >
              詳細
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SummaryCards
// ---------------------------------------------------------------------------

/**
 * サマリーカード
 * @param {object} props
 * @param {DashboardSummaryStats} props.summary サマリー統計
 * @return {JSX.Element} 4つの統計カード
 */
const SummaryCards: FC<{ summary: DashboardSummaryStats }> = ({ summary }) => {
  const pct = Math.round(summary.utilizationRate * 100);
  const winPct = Math.round(summary.winRate * 100);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Utilization Rate */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-4">
          <h3 className="text-xs text-base-content/60 font-medium">稼働率</h3>
          <p className="text-2xl font-bold">{pct}%</p>
          <progress
            className="progress progress-primary w-full"
            value={pct}
            max={100}
          />
          <p className="text-xs text-base-content/50">
            {summary.workingTalents}/{summary.totalTalents}名 稼働中
          </p>
        </div>
      </div>

      {/* Available Talents */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-4">
          <h3 className="text-xs text-base-content/60 font-medium">
            待機タレント
          </h3>
          <p className="text-2xl font-bold">{summary.availableTalents}</p>
          <p className="text-xs text-base-content/50">案件マッチング可能</p>
        </div>
      </div>

      {/* Active Pipelines */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-4">
          <h3 className="text-xs text-base-content/60 font-medium">
            アクティブ案件
          </h3>
          <p className="text-2xl font-bold">{summary.activePipelines}</p>
          <p className="text-xs text-base-content/50">進行中の営業進捗</p>
        </div>
      </div>

      {/* Wins this month */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-4">
          <h3 className="text-xs text-base-content/60 font-medium">
            今月の受注
          </h3>
          <p className="text-2xl font-bold">{summary.wonThisMonth}</p>
          <p className="text-xs text-base-content/50">
            (率: {winPct}% / 失注: {summary.lostThisMonth})
          </p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PipelineFunnel
// ---------------------------------------------------------------------------

/**
 * パイプラインファネル
 * @param {object} props
 * @param {Array} props.stages ファネルステージ一覧
 * @return {JSX.Element} ファネル表示
 */
const PipelineFunnel: FC<{ stages: PipelineFunnelStage[] }> = ({ stages }) => {
  const navigate = useNavigate();
  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body p-4">
        <h2 className="card-title text-sm">営業ファネル</h2>
        <div className="flex flex-col gap-3 mt-2">
          {stages.map(stage => (
            <button
              type="button"
              key={stage.stage}
              className="flex items-center gap-3 cursor-pointer hover:bg-base-200 rounded-lg p-1 transition-colors text-left"
              onClick={() => navigate(`/crm/sales-board?stage=${stage.stage}`)}
            >
              <span className="text-xs w-24 shrink-0 text-base-content/70">
                {stage.label}
              </span>
              <progress
                className="progress progress-primary flex-1"
                value={stage.count}
                max={maxCount}
              />
              <span className="text-sm font-semibold w-8 text-right">
                {stage.count}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ExpiringContracts
// ---------------------------------------------------------------------------

const URGENCY_BADGE: Record<string, string> = {
  critical: "badge-error",
  warning: "badge-warning",
  normal: "badge-ghost",
};

/**
 * 契約終了予定テーブル
 * @param {object} props
 * @param {Array} props.contracts 契約一覧
 * @return {JSX.Element} 契約テーブル
 */
const ExpiringContractsTable: FC<{ contracts: ExpiringContract[] }> = ({
  contracts,
}) => {
  const navigate = useNavigate();

  if (contracts.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-4">
          <h2 className="card-title text-sm">契約終了予定</h2>
          <p className="text-sm text-base-content/50 mt-2">
            今後3ヶ月の契約終了予定はありません
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body p-4">
        <h2 className="card-title text-sm">契約終了予定</h2>
        <div className="overflow-x-auto mt-2">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>タレント</th>
                <th>企業</th>
                <th>終了日</th>
                <th>残日数</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map(c => (
                <tr
                  key={c.contractId}
                  className="hover cursor-pointer"
                  onClick={() =>
                    navigate(`/crm/talents/${c.talentId}?tab=contract`)
                  }
                >
                  <td>{c.talentName}</td>
                  <td>{c.clientCompany}</td>
                  <td className="text-xs">{formatDate(c.endDate)}</td>
                  <td>
                    <span
                      className={`badge badge-sm ${URGENCY_BADGE[c.urgency] ?? "badge-ghost"}`}
                    >
                      {c.remainingDays}日
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AnalyticsPanel
// ---------------------------------------------------------------------------

/**
 * 分析パネル（担当者パフォーマンステーブル行）
 * @param {object} props
 * @param {AssigneePerformance} props.a 担当者データ
 * @return {JSX.Element} テーブル行
 */
const AssigneeRow: FC<{ a: AssigneePerformance }> = ({ a }) => (
  <tr>
    <td>{a.userName}</td>
    <td className="text-center">{a.activePipelines}</td>
    <td className="text-center">{a.wonCount}</td>
    <td className="text-center">{a.lostCount}</td>
    <td className="text-center">
      {a.workingCount}/{a.talentCount}
    </td>
  </tr>
);

/**
 * 詳細分析パネル
 * @param {object} props
 * @param {DashboardAnalyticsResponse | null} props.data 分析データ
 * @param {boolean} props.loading 読み込み中か
 * @param {Error | null} props.error エラー
 * @return {JSX.Element} 分析パネル
 */
const AnalyticsContent: FC<{
  data: DashboardAnalyticsResponse | null;
  loading: boolean;
  error: Error | null;
}> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error text-sm">
        分析データの取得に失敗しました: {error.message}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const maxSkillCount = Math.max(...data.topSkills.map(s => s.count), 1);
  const maxRate = Math.max(...data.utilizationHistory.map(h => h.rate), 0.01);

  return (
    <div className="flex flex-col gap-6">
      {/* Utilization History */}
      <div>
        <h3 className="text-sm font-semibold mb-2">稼働率推移（6ヶ月）</h3>
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>月</th>
                <th>稼働率</th>
                <th className="w-1/2" />
                <th>稼働/全体</th>
              </tr>
            </thead>
            <tbody>
              {data.utilizationHistory.map(h => (
                <tr key={h.month}>
                  <td className="text-xs">{h.month}</td>
                  <td className="text-xs font-semibold">
                    {Math.round(h.rate * 100)}%
                  </td>
                  <td>
                    <progress
                      className="progress progress-accent w-full"
                      value={h.rate}
                      max={maxRate}
                    />
                  </td>
                  <td className="text-xs">
                    {h.working}/{h.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Skills */}
      <div>
        <h3 className="text-sm font-semibold mb-2">トップスキル</h3>
        <div className="flex flex-col gap-2">
          {data.topSkills.map(skill => (
            <div key={skill.skillName} className="flex items-center gap-3">
              <span className="text-xs w-20 shrink-0">{skill.skillName}</span>
              <progress
                className="progress progress-secondary flex-1"
                value={skill.count}
                max={maxSkillCount}
              />
              <span className="text-xs w-6 text-right">{skill.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Financials */}
      {data.financials && (
        <div>
          <h3 className="text-sm font-semibold mb-2">報酬・マージン</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="stat bg-base-200 rounded-lg p-3">
              <div className="stat-title text-xs">平均報酬単価</div>
              <div className="stat-value text-lg">
                {data.financials.averageReward}万
              </div>
            </div>
            <div className="stat bg-base-200 rounded-lg p-3">
              <div className="stat-title text-xs">平均マージン</div>
              <div className="stat-value text-lg">
                {data.financials.averageMargin}万
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignee Performance */}
      {data.assigneePerformance && data.assigneePerformance.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">担当者パフォーマンス</h3>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>担当者</th>
                  <th className="text-center">進行中</th>
                  <th className="text-center">受注</th>
                  <th className="text-center">失注</th>
                  <th className="text-center">稼働/管理</th>
                </tr>
              </thead>
              <tbody>
                {data.assigneePerformance.map(a => (
                  <AssigneeRow key={a.userId} a={a} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// QuickLinks
// ---------------------------------------------------------------------------

/**
 * クイックリンク
 * @return {JSX.Element} ナビゲーションリンク
 */
const QuickLinks: FC = () => {
  const navigate = useNavigate();

  const links = [
    { label: "メール検索", path: "/search" },
    { label: "営業ボード", path: "/crm/sales-board" },
    { label: "タレント管理", path: "/crm/talents" },
  ];

  return (
    <div className="flex flex-wrap gap-3 mt-6">
      {links.map(link => (
        <Button
          key={link.path}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => navigate(link.path)}
        >
          {link.label}
        </Button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CrmDashboard (main)
// ---------------------------------------------------------------------------

/**
 * CRM KPI ダッシュボード
 * @return {JSX.Element} ダッシュボード全体
 */
const CrmDashboard: FC = () => {
  const { data, loading, error, refetch } = useDashboardSummary();
  const analytics = useDashboardAnalytics();
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  const handleToggleAnalytics = useCallback(() => {
    setAnalyticsOpen(prev => {
      const next = !prev;
      if (next && !analytics.data && !analytics.loading) {
        analytics.refetch();
      }
      return next;
    });
  }, [analytics]);

  if (loading) {
    return <FullScreenLoading />;
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="alert alert-error">
          ダッシュボードデータの取得に失敗しました。
          <Button type="button" variant="ghost" size="sm" onClick={refetch}>
            再試行
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <h1 className="text-xl font-bold mb-4">CRM ダッシュボード</h1>

      {/* Alerts */}
      <AlertWidget alerts={data.alerts} />

      {/* Summary cards */}
      <SummaryCards summary={data.summary} />

      {/* Two-column: Funnel + Contracts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <PipelineFunnel stages={data.pipelineFunnel} />
        <ExpiringContractsTable contracts={data.expiringContracts} />
      </div>

      {/* Analytics collapse */}
      <div className="collapse collapse-arrow bg-base-100 shadow-sm mb-6">
        <input
          type="checkbox"
          checked={analyticsOpen}
          onChange={handleToggleAnalytics}
        />
        <div className="collapse-title text-sm font-medium">詳細分析を表示</div>
        <div className="collapse-content">
          {analyticsOpen && (
            <AnalyticsContent
              data={analytics.data}
              loading={analytics.loading}
              error={analytics.error}
            />
          )}
        </div>
      </div>

      {/* Quick links */}
      <QuickLinks />
    </div>
  );
};

export default CrmDashboard;
